import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  construirPlantilla, leerPlantilla, reconciliarPlantilla,
  type FilaPlantilla, type ReconciliacionPlantilla,
} from '../domain/excel/plantillaHorarios';
import { emparejarTienda } from '../domain/pdvMatch';
import { importarHorarios, type FilaHorarioImportada } from '../api/campaigns';
import { listPdv } from '../api/pdv';
import type { CampaignRow, PdvRow } from '../lib/db-types';

/** Una fila de la plantilla ya cruzada contra la maestra de tiendas. */
interface FilaCruzada {
  fila: FilaPlantilla;
  pdv: PdvRow | null;
}

/**
 * Carga la programación de una campaña desde una grilla tienda × fecha.
 *
 * Pedido del usuario (2026-08-15): *"cada fila una tienda y las columnas una fecha […] algo fácil
 * de usar y rápidamente que se pueda subir"*.
 *
 * LA PLANTILLA SE DESCARGA DE ACÁ, con las tiendas y las fechas ya escritas. Eso quita de raíz los
 * dos fallos caros del importador libre: el nombre de tienda que no cruza y el calendario corrido.
 * La persona solo marca. El importador flexible sigue existiendo para lo que manda el cliente; esto
 * es la vía rápida para una campaña que ya está creada.
 *
 * NO SE CONFIRMA SI NO CUADRA (`CARGA_PROGRAMACION.md` §0): el recuadro muestra las marcas leídas y
 * las jornadas que se van a crear, y el botón se habilita solo cuando no hay tiendas sin cruzar ni
 * errores. Una carga que no cuadra no se arregla cambiando el número: se investiga.
 */
export default function ImportarPlantillaHorarios({
  campaign, onCargado,
}: {
  campaign: CampaignRow;
  onCargado: () => void;
}) {
  const [pdvs, setPdvs] = useState<PdvRow[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [cruzadas, setCruzadas] = useState<FilaCruzada[] | null>(null);
  const [recon, setRecon] = useState<ReconciliacionPlantilla | null>(null);
  const [errores, setErrores] = useState<string[]>([]);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Horario y tarifa NO vienen en la grilla: son iguales para toda la carga y se piden acá.
  // Meterlos en la plantilla obligaría a repetirlos en cada fila y a validarlos 30 veces.
  const [inicio, setInicio] = useState('09:00');
  const [fin, setFin] = useState('18:00');
  const [tarifa, setTarifa] = useState('');
  const [unidad, setUnidad] = useState<'jornada' | 'hora'>('jornada');

  useEffect(() => {
    listPdv('').then((rows) => setPdvs(rows.filter((p) => p.active))).catch(() => setPdvs([]));
  }, []);

  function descargar(): void {
    const grilla = construirPlantilla(
      pdvs.map((p) => ({ code: p.code, name: p.name })),
      campaign.start_date,
      campaign.end_date,
    );
    const hoja = XLSX.utils.aoa_to_sheet(grilla);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Programación');
    const nombre = `plantilla-${(campaign.name ?? campaign.client).replace(/[^\w-]+/g, '_')}.xlsx`;
    XLSX.writeFile(libro, nombre);
  }

  async function alSubir(archivo: File): Promise<void> {
    setError(null); setAviso(null); setCruzadas(null); setRecon(null);
    try {
      const libro = XLSX.read(await archivo.arrayBuffer(), { type: 'array' });
      const hoja = libro.Sheets[libro.SheetNames[0]];
      const matriz = XLSX.utils.sheet_to_json<unknown[]>(hoja, { header: 1, raw: false, defval: '' });

      const lectura = leerPlantilla(matriz);
      setErrores(lectura.errores);
      setAvisos(lectura.avisos);
      if (lectura.filas.length === 0) return;

      // El nombre viene como "T10022 — Makro Santa Anita" porque la plantilla la generamos
      // nosotros: se cruza primero por código, que es exacto, y solo si no hay se usa el
      // emparejador tolerante del importador libre.
      const cruce = lectura.filas.map<FilaCruzada>((fila) => {
        const codigo = fila.tienda.split('—')[0].trim();
        const porCodigo = pdvs.find((p) => p.code.toLowerCase() === codigo.toLowerCase());
        return { fila, pdv: porCodigo ?? emparejarTienda(fila.tienda, pdvs) };
      });
      setCruzadas(cruce);
      setRecon(reconciliarPlantilla(lectura.filas));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer el archivo.');
    }
  }

  const sinCruzar = (cruzadas ?? []).filter((c) => c.pdv === null);
  const tarifaNum = Number(tarifa);
  const tarifaOk = Number.isFinite(tarifaNum) && tarifaNum > 0;
  const puedeConfirmar =
    cruzadas !== null && cruzadas.length > 0 && sinCruzar.length === 0
    && errores.length === 0 && tarifaOk && inicio < fin && !guardando;

  async function confirmar(): Promise<void> {
    if (cruzadas === null || recon === null) return;
    setGuardando(true); setError(null);
    try {
      const filas: FilaHorarioImportada[] = cruzadas.map((c) => {
        const info = recon.porTienda.find((t) => t.tienda === c.fila.tienda);
        return {
          pdvId: c.pdv!.id,
          weekdays: info?.weekdays ?? [],
          startTime: inicio,
          endTime: fin,
          headcount: c.fila.recursos,
          rateAmount: tarifaNum,
          rateUnit: unidad,
          fechas: c.fila.fechas,
        };
      });
      const r = await importarHorarios(campaign.id, filas);
      setAviso(`Cargado: ${r.horarios} horario(s) y ${r.jornadas} jornada(s).`);
      setCruzadas(null); setRecon(null); setAvisos([]);
      onCargado();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la programación.');
    } finally {
      setGuardando(false);
    }
  }

  if (!abierto) {
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="rounded border border-blue-600 px-3 py-2 text-sm font-medium text-blue-700"
      >
        Importar plantilla de horarios
      </button>
    );
  }

  return (
    <div className="rounded border border-blue-200 bg-blue-50/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-medium">Importar plantilla de horarios</h3>
        <button type="button" className="text-sm underline" onClick={() => setAbierto(false)}>
          Cerrar
        </button>
      </div>

      <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm text-gray-700">
        <li>
          Descarga la plantilla: trae una fila por tienda y una columna por día del{' '}
          {campaign.start_date} al {campaign.end_date}.
        </li>
        <li>Marca con <strong>1</strong> los días que trabaja cada tienda y borra las filas que no van.</li>
        <li>Súbela acá. Antes de crear nada se muestra cuántas jornadas van a quedar.</li>
      </ol>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <button type="button" onClick={descargar} className="rounded bg-blue-600 px-3 py-2 text-sm text-white">
          Descargar plantilla
        </button>
        <label className="text-sm">
          Horario
          <div className="mt-1 flex items-center gap-1">
            <input type="time" value={inicio} onChange={(e) => setInicio(e.target.value)}
              className="rounded border px-2 py-1" />
            <span>a</span>
            <input type="time" value={fin} onChange={(e) => setFin(e.target.value)}
              className="rounded border px-2 py-1" />
          </div>
        </label>
        <label className="text-sm">
          Tarifa <span className="text-red-600">*</span>
          <input type="number" min="1" step="0.5" value={tarifa}
            onChange={(e) => setTarifa(e.target.value)}
            className="mt-1 block w-28 rounded border px-2 py-1" />
        </label>
        <label className="text-sm">
          Unidad
          <select value={unidad} onChange={(e) => setUnidad(e.target.value as 'jornada' | 'hora')}
            className="mt-1 block rounded border px-2 py-1">
            <option value="jornada">Por jornada</option>
            <option value="hora">Por hora</option>
          </select>
        </label>
      </div>

      <label className="mb-3 block text-sm">
        Subir plantilla llena
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void alSubir(f); }}
          className="mt-1 block w-full rounded border bg-white px-2 py-1"
        />
      </label>

      {inicio >= fin && (
        <p className="mb-2 text-sm text-red-700">La hora de fin tiene que ser posterior a la de inicio.</p>
      )}
      {error && <p className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {aviso && <p className="mb-2 rounded border border-green-200 bg-green-50 p-2 text-sm text-green-800">{aviso}</p>}

      {errores.length > 0 && (
        <ul className="mb-2 list-disc space-y-1 rounded border border-red-200 bg-red-50 p-2 pl-6 text-sm text-red-700">
          {errores.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}
      {avisos.length > 0 && (
        <ul className="mb-2 list-disc space-y-1 rounded border border-amber-200 bg-amber-50 p-2 pl-6 text-sm text-amber-800">
          {avisos.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}

      {recon !== null && cruzadas !== null && (
        <div className="rounded border bg-white p-3">
          {/* El número contra el que se cuadra, ANTES de crear nada. */}
          <p className="text-sm">
            Se leyeron <strong>{recon.diasTienda}</strong> marcas en{' '}
            <strong>{recon.tiendas}</strong> tienda(s) y se van a crear{' '}
            <strong>{recon.jornadas}</strong> jornada(s).
            {recon.jornadas !== recon.diasTienda
              && ' La diferencia son las tiendas que piden más de un recurso por día.'}
          </p>

          {sinCruzar.length > 0 && (
            <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
              <p className="font-medium">
                {sinCruzar.length} tienda(s) no se pudieron cruzar con la maestra:
              </p>
              <ul className="list-disc pl-5">
                {sinCruzar.map((c) => <li key={c.fila.tienda}>{c.fila.tienda}</li>)}
              </ul>
              {/* Emparejar mal es peor que no emparejar: las jornadas se cargan en la tienda
                  equivocada y nadie lo nota hasta que alguien reclame el pago. */}
              <p className="mt-1">
                No se carga nada hasta resolverlas. Corrige el nombre en la plantilla usando el
                que trae la descarga, o pide que se den de alta en PDV.
              </p>
            </div>
          )}

          <table className="mt-2 w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr><th className="p-1">Tienda</th><th className="p-1">Días</th>
                <th className="p-1">Recursos</th><th className="p-1">Jornadas</th></tr>
            </thead>
            <tbody>
              {recon.porTienda.map((t) => {
                const c = cruzadas.find((x) => x.fila.tienda === t.tienda);
                return (
                  <tr key={t.tienda} className="border-t">
                    <td className="p-1">
                      {c?.pdv ? c.pdv.name : <span className="text-red-700">{t.tienda} — sin cruce</span>}
                    </td>
                    <td className="p-1">{t.dias}</td>
                    <td className="p-1">{c?.fila.recursos ?? 1}</td>
                    <td className="p-1">{t.jornadas}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={!puedeConfirmar}
              onClick={() => void confirmar()}
              className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {guardando ? 'Cargando…' : `Confirmar ${recon.jornadas} jornada(s)`}
            </button>
            {!tarifaOk && <span className="text-sm text-red-700">Falta la tarifa.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
